import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { API } from '@/config/api';

// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      
      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const response = await axios.post(`${API}/auth/login`, { email, password });
          const { access_token, user } = response.data;
          set({ user, token: access_token, isAuthenticated: true, isLoading: false });
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: error.response?.data?.detail || 'Login failed' };
        }
      },
      
      loginWithGoogle: async (googleUserData) => {
        set({ isLoading: true });
        try {
          const response = await axios.post(`${API}/auth/google`, googleUserData);
          const { access_token, user } = response.data;
          set({ user, token: access_token, isAuthenticated: true, isLoading: false });
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: error.response?.data?.detail || 'Google login failed' };
        }
      },
      
      register: async (email, password, full_name, phone) => {
        set({ isLoading: true });
        try {
          const response = await axios.post(`${API}/auth/register`, { email, password, full_name, phone });
          const { access_token, user } = response.data;
          set({ user, token: access_token, isAuthenticated: true, isLoading: false });
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: error.response?.data?.detail || 'Registration failed' };
        }
      },
      
      logout: () => {
        set({ user: null, token: null, isAuthenticated: false });
      },
      
      updateProfile: async (data) => {
        const { token } = get();
        try {
          const response = await axios.put(`${API}/auth/profile`, data, {
            headers: { Authorization: `Bearer ${token}` }
          });
          set({ user: response.data });
          return { success: true };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Update failed' };
        }
      },
      
      getAuthHeader: () => {
        const { token } = get();
        return token ? { Authorization: `Bearer ${token}` } : {};
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated })
    }
  )
);

// Cart Store
export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],
      restaurantId: null,
      restaurantName: null,
      subtotal: 0,
      itemCount: 0,
      isLoading: false,
      
      fetchCart: async () => {
        const token = useAuthStore.getState().token;
        if (!token) return;
        
        set({ isLoading: true });
        try {
          const response = await axios.get(`${API}/cart`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          set({
            items: response.data.items,
            restaurantId: response.data.restaurant_id,
            restaurantName: response.data.restaurant_name,
            subtotal: response.data.subtotal,
            itemCount: response.data.item_count,
            isLoading: false
          });
        } catch (error) {
          set({ isLoading: false });
          // a saved customer sign-in the server no longer accepts (expired, or
          // the signing key changed): clear it instead of asking again forever
          if (error.response?.status === 401) {
            useAuthStore.getState().logout();
          }
        }
      },
      
      addItem: async (menuItemId, quantity = 1, specialInstructions = null, selectedModifiers = null) => {
        const token = useAuthStore.getState().token;
        if (!token) return { success: false, error: 'Please login first' };
        
        try {
          await axios.post(`${API}/cart/items`, {
            menu_item_id: menuItemId,
            quantity,
            special_instructions: specialInstructions,
            selected_modifiers: selectedModifiers
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          await get().fetchCart();
          return { success: true };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to add item' };
        }
      },
      
      updateItem: async (itemId, quantity) => {
        const token = useAuthStore.getState().token;
        if (!token) return;
        
        try {
          await axios.put(`${API}/cart/items/${itemId}?quantity=${quantity}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          await get().fetchCart();
        } catch (error) {
          console.error('Failed to update cart item:', error);
        }
      },
      
      removeItem: async (itemId) => {
        const token = useAuthStore.getState().token;
        if (!token) return;
        
        try {
          await axios.delete(`${API}/cart/items/${itemId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          await get().fetchCart();
        } catch (error) {
          console.error('Failed to remove cart item:', error);
        }
      },
      
      clearCart: async () => {
        const token = useAuthStore.getState().token;
        if (!token) return;
        
        try {
          await axios.delete(`${API}/cart`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          set({ items: [], restaurantId: null, restaurantName: null, subtotal: 0, itemCount: 0 });
        } catch (error) {
          console.error('Failed to clear cart:', error);
        }
      }
    }),
    {
      name: 'cart-storage',
      partialize: (state) => ({})  // Don't persist cart, always fetch from server
    }
  )
);

// Theme Store
export const useThemeStore = create(
  persist(
    (set) => ({
      theme: 'light',
      toggleTheme: () => set((state) => {
        const newTheme = state.theme === 'light' ? 'dark' : 'light';
        document.documentElement.classList.toggle('dark', newTheme === 'dark');
        return { theme: newTheme };
      }),
      setTheme: (theme) => {
        document.documentElement.classList.toggle('dark', theme === 'dark');
        set({ theme });
      }
    }),
    {
      name: 'theme-storage'
    }
  )
);
